import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActiveCartFacade, Cart, PaymentType, PromotionLocation } from "@spartacus/cart/base/root";
import { Card, ICON_TYPE } from "@spartacus/storefront";
import { CheckoutDeliveryAddressFacade, CheckoutPaymentFacade, CheckoutStep, CheckoutStepType } from "@spartacus/checkout/base/root";
import { Address, PaymentDetails, TranslationService, WindowRef } from "@spartacus/core";
import { CheckoutStepService } from "@spartacus/checkout/base/components";
import { combineLatest, Observable, Subject } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { isEqual } from 'src/app/generac/shared/helpers/is-equal';
import { concatStrings } from 'src/app/generac/shared/helpers/concat-strings';
import { GenHandlingOption } from 'src/app/generac/models/cart.model';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { SubscriptionsService } from 'src/app/generac/services/subscriptions.service';
import moment from 'moment';
import { GenCartType } from 'src/app/generac/enums/cart-type.enum';
import { GenEngineReplacementForm } from 'src/app/generac/models/checkout.model';

@Component({
  selector: 'gen-review-submit',
  templateUrl: './checkout-review-submit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenCheckoutReviewSubmitComponent implements OnInit {
  iconTypes = ICON_TYPE;
  isSubscription = false;
  isCartMixed: boolean;
  checkoutStepTypeDeliveryAddress = CheckoutStepType.DELIVERY_ADDRESS;
  checkoutStepTypePaymentType = CheckoutStepType.PAYMENT_TYPE;
  checkoutStepTypeSpecialHandling = CheckoutStepType.SPECIAL_HANDLING;
  checkoutStepTypeSubscriptionInformation = CheckoutStepType.SUBSCRIPTION_INFORMATION;
  checkoutStepTypeServiceParts = CheckoutStepType.SERVICE_PARTS;
  checkoutStepTypeEngineReplacementForm = CheckoutStepType.ENGINE_REPLACEMENT_FORM;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;
  cart$: Observable<Cart>;
  entries$: Observable<any[]>;
  isServicePartsEnabled: boolean;
  GenCartType = GenCartType;
  private destroy$ = new Subject<void>();

  steps$: Observable<CheckoutStep[]> = this.checkoutStepService.steps$;

  deliveryAddress$: Observable<Address | undefined> =
    this.checkoutDeliveryAddressFacade.getDeliveryAddressState().pipe(
      filter((state) => !state.loading && !state.error),
      map((state) => state.data)
    );

  paymentDetails$: Observable<PaymentDetails | undefined> =
    this.checkoutPaymentFacade.getPaymentDetailsState().pipe(
      filter((state) => !state.loading && !state.error),
      map((state) => state.data),
    );

  engineReplacementForm$: Observable<GenEngineReplacementForm>;

  constructor(
    protected checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
    protected checkoutPaymentFacade: CheckoutPaymentFacade,
    protected activeCartFacade: ActiveCartFacade,
    protected translationService: TranslationService,
    protected checkoutStepService: CheckoutStepService,
    protected subscriptionsService: SubscriptionsService,
    protected winRef: WindowRef,
    private checkoutService: GenCheckoutService,
  ) {
    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.PAYMENT_TYPE, false
    );
  }

  ngOnInit() {
    this.setCart();
    this.setEngineReplacementForm();
    this.createEntryGroupsStream();
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
  }

  getDeliveryAddressCard(
    deliveryAddress: Address,
    countryName?: string
  ): Observable<Card> {
    const deliveryTranslation$ = this.isSubscription ?
      this.translationService.translate('addressCard.deliveryAddress') :
      this.translationService.translate('addressCard.shipTo');
    return combineLatest([
      deliveryTranslation$,
      this.translationService.translate('addressCard.phoneNumber'),
      this.translationService.translate('addressCard.mobileNumber'),
    ]).pipe(
      map(([textTitle, textPhone, textMobile]) => {
        if (!countryName) {
          countryName = deliveryAddress?.country?.name as string;
        }
        const numbers = deliveryAddress.phone;
        return {
          title: textTitle,
          text: [
            deliveryAddress.companyName,
            concatStrings(
              ' ',
              deliveryAddress?.firstName,
              deliveryAddress?.lastName
            ),
            deliveryAddress.line1,
            deliveryAddress.line2,
            concatStrings(
              ', ',
              deliveryAddress.town,
              deliveryAddress.region?.name,
              deliveryAddress?.country?.isocode
            ),
            deliveryAddress.postalCode,
            numbers,
          ],
        } as Card;
      })
    );
  }

  getFreightForwarderCard(freightForwarderAddress: Address): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutAddress.ffAddress'),
      this.translationService.translate('addressCard.phoneNumber'),
      this.translationService.translate('addressCard.mobileNumber'),
    ]).pipe(
      map(([textTitle, textPhone, textMobile]) => {
        const numbers = freightForwarderAddress.phone;
        return {
          title: textTitle,
          text: [
            freightForwarderAddress.companyName,
            freightForwarderAddress.firstName,
            freightForwarderAddress.line1,
            freightForwarderAddress.line2,
            concatStrings(
              ', ',
              freightForwarderAddress.town,
              freightForwarderAddress.region?.name,
              freightForwarderAddress?.country?.isocode
            ),
            freightForwarderAddress.postalCode,
            numbers,
          ],
        } as Card;
      })
    );
  }

  getPaymentMethodCard(paymentDetails: PaymentDetails): Observable<Card> {
    return combineLatest([
      this.translationService.translate('paymentForm.payment'),
      this.translationService.translate('paymentCard.expires', {
        month: paymentDetails.expiryMonth,
        year: paymentDetails.expiryYear,
      }),
      this.translationService.translate('paymentForm.billingAddress'),
    ]).pipe(
      map(([textTitle, textExpires, billingAddress]) => {
        return {
          title: textTitle,
          textBold: paymentDetails.accountHolderName,
          text: [paymentDetails.cardNumber, textExpires],
          paragraphs: [
            {
              title: billingAddress + ':',
              text: [
                concatStrings(
                  ' ',
                  paymentDetails.billingAddress?.firstName,
                  paymentDetails.billingAddress?.lastName
                ),
                paymentDetails.billingAddress?.line1,
                concatStrings(
                  ', ',
                  paymentDetails.billingAddress?.town,
                  paymentDetails.billingAddress?.region?.isocode,
                  paymentDetails.billingAddress?.country?.isocode
                ),
                paymentDetails.billingAddress?.postalCode,
              ],
            },
          ],
        } as Card;
      })
    );
  }

  getCheckoutStepUrl(stepType: CheckoutStepType | string, shippingGroupName?: string): string | undefined {
    const step = this.checkoutStepService.getCheckoutStep(
      stepType as CheckoutStepType
    );
    if (stepType == this.checkoutStepTypeSpecialHandling && this.isServicePartsEnabled && shippingGroupName) {
      return shippingGroupName == GenCartType.FINISHED_GOODS ? 'checkoutFinishedGoods' : 'checkoutServiceParts';
    }
    return step?.routeName;
  }

  getPaymentTypeCard(paymentType: PaymentType): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.paymentDetails'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [paymentType.displayName],
        } as Card;
      })
    );
  }

  getPurchaseOrderNumber(purchaseOrderNumber: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutAddress.detailsForm.poNumber'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [purchaseOrderNumber ? purchaseOrderNumber : 'None']
        } as Card;
      })
    );
  }

  getEndDateCard(endDate: string): Observable<Card> {
    const formattedDate = new Date(endDate).toISOString().split('T')[0];
    return combineLatest([
      this.translationService.translate('checkoutReview.endDate'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [formattedDate]
        } as Card;
      })
    );
  }

  getFrequencyCard(frequency: number): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutReview.frequency'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [`Every ${frequency} days`]
        } as Card;
      })
    );
  }

  getDesiredDateCard(desiredDate: string): Observable<Card> {
    const formattedDate = moment(desiredDate).format('YYYY-MM-DD');
    return combineLatest([
      this.translationService.translate('checkoutReview.desiredDate'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [formattedDate]
        } as Card;
      })
    );
  }

  getNextOrderCard(replenishment: any): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutReview.nextOrder'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [this.addDays(new Date(replenishment.startDate), replenishment.interval).toISOString().split('T')[0]]
        } as Card;
      })
    );
  }

  getSpecialHandlingCard(handlingOptions: GenHandlingOption[]): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.specialHandlingTab'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: handlingOptions.map(option => option.name),
        } as Card;
      })
    );
  }

  getEngineReplacementModelNumberCard(engineModelNumber: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.modelNumberDestroyed'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [
            engineModelNumber
          ],
        } as Card;
      })
    );
  }

  getEngineReplacementSerialNumberCard(engineSerialNumber: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.serialNumberDestroyed'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [
            engineSerialNumber
          ],
        } as Card;
      })
    );
  }

  getEngineReplacementAccountIdCard(accountId: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.accountId'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [
            accountId
          ],
        } as Card;
      })
    );
  }

  getEngineReplacementPurchaseDateCard(purchaseDate: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.purchaseDate'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [
            moment(purchaseDate).format('MM.DD.YYYY')
          ],
        } as Card;
      })
    );
  }

  getEngineReplacementAttestationCheckboxesCard(replacementReason: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.attestationCheckboxesTitle'),
      this.translationService.translate('checkoutProgress.engineReplacementForm.attestationCheckboxesOptions.' + replacementReason),
    ]).pipe(
      map(([textTitle, replacementReason]) => {
        return {
          title: textTitle,
          text: [
            replacementReason
          ],
        } as Card;
      })
    );
  }

  getEngineReplacementPurchasingUserCard(purchasingUser: string): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.purchasingUser'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [
            purchasingUser
          ],
        } as Card;
      })
    );
  }

  getEngineReplacementDestroyedByCard(engineReplacementForm: GenEngineReplacementForm): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.engineReplacementForm.destroyedByText'),
      this.translationService.translate(`checkoutProgress.engineReplacementForm.destroyedByOptions.${engineReplacementForm.destructionType}`),
    ]).pipe(
      map(([textTitle, destructionType]) => {
        return {
          title: textTitle,
          text: [
            destructionType
          ],
        } as Card;
      })
    );
  }

  private setCart() {
    this.cart$ = this.activeCartFacade.takeActive()
      .pipe(
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$),
        tap(cart => {
          this.isSubscription = cart.cartTypes?.length === 1 && cart.cartTypes[0] === GenCartType.SUBSCRIPTION.toString();
          this.checkoutService.disableCheckoutSteps(cart?.cartTypes, cart?.engineReplacement);
        })
      );
  }

  private setEngineReplacementForm(): void {
    this.engineReplacementForm$ = this.cart$.pipe(
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$),
        switchMap(cart =>
          this.checkoutService.getEngineReplacementAttestation(cart.code, cart.user?.uid)
        )
    )
  }

  private createEntryGroupsStream() {
    this.entries$ = this.activeCartFacade
      .getActive()
      .pipe(
        takeUntil(this.destroy$),
        map((cart: any) => cart.entryGroups),
        filter((entryGroups) => entryGroups?.length > 0)
      )
  }

  private addDays(date: Date, days: number): Date {
    date.setDate(date.getDate() + days);
    return date;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
